import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import AddToPaymentModal from 'modules/expenses/components/expenses/AddToPaymentModal'
import AddToPayrollModal from 'modules/expenses/components/expenses/AddToPayrollModal'
import RemoveFromPayrollModal from 'modules/expenses/components/expenses/RemoveFromPayrollModal'
import RestartApprovalFlowModal from 'modules/expenses/components/expenses/RestartApprovalFlowModal'
import ReviewModal from 'modules/expenses/components/expenses/ReviewModal'
import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'
import { expensableResource } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'
import { PayrollExpensableDetail } from 'modules/expenses/types/PayrollExpensableDetail'

import { ConfirmationModal as Modal, useConfirmationModal } from './hooks/useBulkActions'

interface ConfirmationModalsProps {
  expensable: Expensable
  payroll: PayrollExpensableDetail
}

const ConfirmationModals = ({
  expensable,
  payroll: { categories, currentPeriodOpen },
}: ConfirmationModalsProps) => {
  const {
    onApprove,
    onReject,
    onAddToPayroll,
    onRemoveFromPayroll,
    onRestartReview,
    onAddToPayment,
  } = useExpensableActions()

  const { modal, closeModal, submit } = useConfirmationModal()

  const expensableCategory = expensableResource(expensable)?.category

  return (
    <>
      {modal === Modal.GlobalApprove && (
        <ReviewModal
          type="approve"
          title={i18n.t('expenses_v2.list.actions.modal.title_global_approve')}
          description={i18n.t('expenses_v2.list.actions.modal.description_global_approve')}
          onSubmit={() => submit('approve', onApprove([expensable.id], true))}
          onClose={closeModal}
        />
      )}

      {modal === Modal.Reject && (
        <ReviewModal
          type="reject"
          onSubmit={(reason) => submit('reject', onReject([expensable.id], reason))}
          onClose={closeModal}
        />
      )}

      {modal === Modal.GlobalReject && (
        <ReviewModal
          type="reject"
          onSubmit={(reason) => submit('reject', onReject([expensable.id], reason, true))}
          onClose={closeModal}
        />
      )}

      {modal === Modal.RestartApprovalFlow && (
        <RestartApprovalFlowModal
          onSubmit={() => submit('restart_approval_flow', onRestartReview([expensable.id]))}
          onClose={closeModal}
        />
      )}

      {modal === Modal.AddToPayroll && (
        <AddToPayrollModal
          currentPeriodOpen={currentPeriodOpen}
          availableCategories={categories}
          selectedCategories={expensableCategory ? [expensableCategory.id] : []}
          onSubmit={(period) =>
            submit('create_payroll_supplement', onAddToPayroll(period, [expensable.id]))
          }
          onClose={closeModal}
        />
      )}

      {modal === Modal.AddToPayment && (
        <AddToPaymentModal
          expensables={[expensable]}
          onSubmit={(ids: number[], dueDate: Date) =>
            submit('create_payment', onAddToPayment(ids, dueDate))
          }
          onClose={closeModal}
        />
      )}

      {modal === Modal.RemoveFromPayroll && (
        <RemoveFromPayrollModal
          onSubmit={() => submit('remove_payroll_supplement', onRemoveFromPayroll([expensable.id]))}
          onClose={closeModal}
        />
      )}
    </>
  )
}

export default observer(ConfirmationModals)
